import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  size,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole,
} from "@floating-ui/react";
import clsx from "clsx";
import {
  forwardRef,
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useInView } from "react-intersection-observer";
import { mergeRefs } from "react-merge-refs";

import { Box } from "@atoms/box";
import { DropdownItem } from "@atoms/dropdownItem/dropdownItem";
import { DropdownSearch } from "@atoms/dropdownSearch/dropdownSearch";
import { Flag } from "@atoms/flag";
import { Icon } from "@atoms/icon";
import { ButtonLoader } from "@atoms/loader/buttonLoader";
import { Typography } from "@atoms/typography";
import { FastLinkButton } from "@fastFields/fastLinkButton";

import { BaseInput } from "../baseInput/baseInput";
import { HintText } from "../hintText";
import { InputIcon } from "../inputIcon";
import rootClasses from "../styles.module.scss";

import classes from "./styles.module.scss";

import type { Ref } from "react";

import type { IDropdownProps } from "./types";

export const Dropdown = forwardRef(function Dropdown<T = unknown>(
  props: IDropdownProps<T>,
  ref: Ref<HTMLInputElement>,
) {
  const {
    onSearchChange,
    options,
    value = { key: "", value: "" },
    onKeyDown: propsOnKeyDown,
    onValueChange,
    disabled,
    readOnly,
    isInvalid,
    inputSize = "medium",
    noSearch,
    hintText,
    iconName,
    flag,
    isFetchingNextPage,
    isLoading,
    isError,
    onGetNext,
    renderItem,
    rootClassName,
    superRootClassName,
    noClear,
    ...rest
  } = props;

  const { t } = useTranslation("component");

  const inputRef = useRef<HTMLInputElement>(null);

  const [open, rawSetOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const setOpen = useCallback(
    (value: boolean) => {
      if (!readOnly && !disabled) {
        if (value) {
          setSearchInputValue("");
        }
        rawSetOpen(value);
      }
    },
    [disabled, readOnly],
  );

  const { ref: loadMoreContainerRef, inView } = useInView();

  useEffect(() => {
    if (inView) {
      onGetNext?.();
    }
    // !important do not remove this comment
    // and no not add onGetNext to deps otherwise it cause loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView]);

  const [searchInputValue, setSearchInputValue] = useState("");

  const { refs, floatingStyles, context } = useFloating({
    placement: "bottom-start",
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip(),
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
            MinWidth: `300px`,
          });
        },
      }),
    ],
  });

  const listRef = useRef<(HTMLElement | null)[]>([]);

  const dismiss = useDismiss(context);
  const role = useRole(context, {
    role: "listbox",
  });
  const listNav = useListNavigation(context, {
    listRef,
    activeIndex,
    onNavigate: setActiveIndex,
    virtual: true,
    loop: false,
  });

  const { getFloatingProps, getItemProps, getReferenceProps } = useInteractions(
    [dismiss, role, listNav],
  );

  const handleSelect = () => {
    if (activeIndex === null) return;

    const activeItem = options[activeIndex];

    if (activeItem) onValueChange?.(activeItem);
    else throw new Error("activeItem is undefined");
    setActiveIndex(null);
    setOpen(false);
  };

  const clearValue = () => {
    onValueChange?.({
      key: "",
      value: "",
      description: undefined,
      imgUrl: undefined,
      fullData: undefined,
      flag: undefined,
    });
  };

  return (
    <HintText
      disabled={disabled}
      isInvalid={isInvalid}
      text={hintText}
      className={superRootClassName}
    >
      <div
        className={clsx(
          rootClasses.inputBox,
          classes.dropdownInputBox,
          rootClassName,
        )}
        data-isinvalid={isInvalid}
        data-size={inputSize}
        data-read-only={readOnly}
        data-disabled={disabled}
        ref={refs.setReference}
        {...getReferenceProps({
          onMouseDown: (event) => {
            event.preventDefault();
            inputRef.current?.focus();
          },
          onClick: () => {
            setOpen(!open);
          },
        })}
      >
        {iconName && (
          <Icon
            name={iconName}
            className={clsx("icon", classes.flex0)}
            fontSize={"fs-20"}
          />
        )}
        {flag && <Flag flag={flag} />}
        <BaseInput
          readOnly
          disabled={disabled}
          value={value.value}
          ref={mergeRefs([inputRef, ref])}
          onKeyDown={(event) => {
            if (
              !readOnly &&
              !disabled &&
              event.key === "Backspace" &&
              noClear !== true
            ) {
              clearValue();
            }

            propsOnKeyDown?.(event);
          }}
          {...rest}
        />
        {value.key !== "" && !disabled && !readOnly && noClear !== true ? (
          <InputIcon
            tabIndex={-1}
            onClick={() => {
              clearValue();
            }}
          />
        ) : (
          <Icon
            className={clsx("icon", classes.flex0)}
            name={"ArrowDownSLine"}
          />
        )}
      </div>
      <FloatingPortal>
        {open && (
          <FloatingFocusManager
            context={context}
            initialFocus={0}
            visuallyHiddenDismiss
          >
            <Box
              {...getFloatingProps({
                ref: refs.setFloating,
                style: {
                  ...floatingStyles,
                  zIndex: "100",
                  outline: "none",
                },
                onKeyDown: (event) => {
                  if (
                    event.key === "Enter" &&
                    activeIndex != null &&
                    options[activeIndex]
                  ) {
                    event.preventDefault();
                    handleSelect();
                  }
                },
              })}
              gridGap={"8"}
              py={"6"}
            >
              {onSearchChange && noSearch !== true && (
                <DropdownSearch
                  value={searchInputValue}
                  onValueChange={setSearchInputValue}
                  containerClassName={"mx-6"}
                  onDebouncedChange={onSearchChange}
                />
              )}
              <Box
                px={"6"}
                gridGap={"4"}
                noBorder
                style={{ maxHeight: "300px" }}
              >
                {options.length === 0 && !isLoading && !isFetchingNextPage && (
                  <Typography
                    themeColor={"text-soft-400"}
                    fontType={"label-x-small"}
                    textCenter
                  >
                    {t("common.noData")}
                  </Typography>
                )}
                {options.map((item, index) => (
                  <Fragment key={item?.key}>
                    {renderItem ? (
                      renderItem(item, {
                        ...getItemProps({
                          ref(node) {
                            listRef.current[index] = node;
                          },
                          onClick: handleSelect,
                        }),
                        isSelected: value.key === item.key,
                        text: item.value,
                        description: item.description,
                        iconName: item.icon,
                        key: item.key,
                      })
                    ) : (
                      <DropdownItem
                        key={item.key}
                        {...getItemProps({
                          ref(node) {
                            listRef.current[index] = node;
                          },
                          onClick: handleSelect,
                        })}
                        isSelected={value.key === item.key}
                        text={item.value}
                        description={item.description}
                        iconName={item.icon}
                        flag={item.flag}
                      />
                    )}
                  </Fragment>
                ))}
                {(isFetchingNextPage || isLoading) && (
                  <div
                    className={
                      "w-100 d-flex justify-content-center align-items-center"
                    }
                    style={{ height: "2rem" }}
                  >
                    <ButtonLoader />
                  </div>
                )}
                {!!onGetNext && (
                  <div
                    className={"w-100 d-flex justify-content-center"}
                    ref={loadMoreContainerRef}
                  >
                    <FastLinkButton
                      type={"loadMore"}
                      onClick={() => {
                        onGetNext?.();
                      }}
                    />
                  </div>
                )}
              </Box>
            </Box>
          </FloatingFocusManager>
        )}
      </FloatingPortal>
    </HintText>
  );
});
